import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Box,
    CircularProgress,
    TextField,
    InputAdornment,
    useTheme,
    Chip,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesome,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { BackendAgent, fetchAgents } from '../utils/api';
import toast from 'react-hot-toast';

interface AgentSelectionModalProps {
    open: boolean;
    onClose: () => void;
    onSelectAgent: (agent: BackendAgent) => void;
    selectedAgentId?: string | null;
    title?: string;
    subtitle?: string;
}

const AgentSelectionModal: React.FC<AgentSelectionModalProps> = ({
    open,
    onClose,
    onSelectAgent,
    selectedAgentId,
    title = 'Selecionar Agente',
    subtitle = 'Escolha um agente para filtrar as conversas',
}) => {
    const theme = useTheme();
    const [agents, setAgents] = useState<BackendAgent[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAgent, setSelectedAgent] = useState<BackendAgent | null>(null);

    useEffect(() => {
        if (open) {
            loadAgents();
        }
    }, [open]);

    useEffect(() => {
        if (selectedAgentId && agents.length > 0) {
            const agent = agents.find(a => a.id === selectedAgentId);
            setSelectedAgent(agent || null);
        }
    }, [selectedAgentId, agents]);

    const loadAgents = async () => {
        setLoading(true);
        try {
            const agentsList = await fetchAgents();
            // Filter only enabled agents
            const enabledAgents = agentsList.filter(agent => agent.status === 'Enabled');
            setAgents(enabledAgents);
        } catch (error) {
            console.error('Erro ao carregar agentes:', error);
            toast.error('Erro ao carregar agentes');
        } finally {
            setLoading(false);
        }
    };

    const filteredAgents = agents.filter(agent =>
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelectAgent = (agent: BackendAgent) => {
        setSelectedAgent(agent);
    };

    const handleConfirm = () => {
        if (selectedAgent) {
            onSelectAgent(selectedAgent);
            onClose();
        }
    };

    const handleCancel = () => {
        setSelectedAgent(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    maxHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: theme.palette.info.main,
                            width: 48,
                            height: 48,
                        }}
                    >
                        <AutoAwesome />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3 }}>
                {/* Search Field */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar agentes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                            borderRadius: '12px',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme.palette.background.paper,
                            },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Agents List */}
                <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredAgents.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <AutoAwesome sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
                            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                                {searchTerm ? 'Nenhum agente encontrado' : 'Nenhum agente disponível'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                                {searchTerm ? 'Tente ajustar o termo de busca' : 'Não há agentes habilitados no momento'}
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {filteredAgents.map((agent) => (
                                <ListItem key={agent.id} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleSelectAgent(agent)}
                                        selected={selectedAgent?.id === agent.id}
                                        sx={{
                                            borderRadius: '12px',
                                            border: selectedAgent?.id === agent.id
                                                ? `2px solid ${theme.palette.info.main}`
                                                : `1px solid ${theme.palette.divider}`,
                                            backgroundColor: selectedAgent?.id === agent.id
                                                ? (theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.12)' : '#f0f9ff')
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedAgent?.id === agent.id
                                                    ? (theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.2)' : '#e0f2fe')
                                                    : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8fafc'),
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                            px: 2,
                                            py: 1.5,
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    backgroundColor: selectedAgent?.id === agent.id
                                                        ? theme.palette.info.main
                                                        : (theme.palette.mode === 'dark' ? '#313131' : '#64748b'),
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                <AutoAwesome />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                                        {agent.name}
                                                    </Typography>
                                                    {selectedAgent?.id === agent.id && (
                                                        <CheckCircleIcon
                                                            sx={{
                                                                color: theme.palette.info.main,
                                                                fontSize: 20
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <></>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={handleCancel}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!selectedAgent}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        color: theme.palette.mode === 'dark' ? '#000' : undefined ,
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#0284c7' : '#0284c7',
                        },
                    }}
                >
                Confirmar Seleção
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AgentSelectionModal;
